/** Hook for registering global keyboard shortcuts. */

import { useEffect, useCallback, useRef } from 'react'

interface ShortcutOptions {
  /** Key to listen for (e.g. "k", "Escape", "Enter") */
  key: string
  /** Require Ctrl/Cmd modifier */
  ctrl?: boolean
  /** Require Shift modifier */
  shift?: boolean
  /** Require Alt modifier */
  alt?: boolean
  /** Disable when focus is inside an input/textarea */
  ignoreInputs?: boolean
}

/**
 * Register a global keyboard shortcut.
 *
 * @param options - Key and modifier configuration
 * @param callback - Called when the shortcut fires
 */
export function useKeyboardShortcut(
  options: ShortcutOptions,
  callback: (e: KeyboardEvent) => void,
): void {
  const callbackRef = useRef(callback)
  useEffect(() => {
    callbackRef.current = callback
  }, [callback])

  const handler = useCallback(
    (e: KeyboardEvent) => {
      // Skip if modifiers don't match
      if (options.ctrl && !(e.ctrlKey || e.metaKey)) return
      if (options.shift && !e.shiftKey) return
      if (options.alt && !e.altKey) return

      // Skip if wrong key
      if (e.key.toLowerCase() !== options.key.toLowerCase()) return

      // Skip if focused in input and ignoreInputs is set (default true)
      const ignoreInputs = options.ignoreInputs ?? true
      if (ignoreInputs) {
        const tag = (e.target as HTMLElement)?.tagName
        if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return
        if ((e.target as HTMLElement)?.isContentEditable) return
      }

      e.preventDefault()
      callbackRef.current(e)
    },
    [options.key, options.ctrl, options.shift, options.alt, options.ignoreInputs],
  )

  useEffect(() => {
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [handler])
}
